import React, {useState} from 'react';

import {Button, Icon, Input, Text, addGenericErrorToast} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {genericMandarkRequest} from 'resources/mandark.resource';

import {usePerformSignupStep} from '../../usePerformSignupStep';
import OnboardingPageLayout from '../OnboardingPageLayout';

interface NewEnrollCodeStepProps {
  onBack: () => void;
  onSubmit: () => void;
}

export default function NewEnrollCodeStep({onBack, onSubmit}: NewEnrollCodeStepProps) {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const performSignupStep = usePerformSignupStep();

  const handleSubmit = async () => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await genericMandarkRequest(
        'post',
        {
          resourcePath: ['students_v2', sessionStore.getUserId(), 'relationships', 'classrooms_v1']
        },
        {
          data: [
            {
              type: 'classroom_v1',
              meta: {classroom_code: trimmedCode}
            }
          ]
        }
      );

      await performSignupStep({
        parentalConsent: {
          consentMethod: 'enroll_code',
          enrollCode: trimmedCode
        }
      });
      onSubmit();
    } catch (e: any) {
      if (e?.status === 400) {
        setError('There was a problem with your enrollment code, try again!');
      } else {
        addGenericErrorToast();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingPageLayout>
      <Button
        color='primary'
        variant='text'
        size='s'
        onClick={onBack}
        className='flex max-w-fit items-center justify-start gap-x1'
      >
        <Icon icon='arrow-left' />
        <Text color='inherit'>Back</Text>
      </Button>
      <div className='flex max-w-[516px] flex-col gap-x0-half'>
        <Text className='text-xxl font-black' data-testid='enroll-code-step__heading'>
          Enter Class Code
        </Text>
        <Text>
          Join a class by entering a class code below. If you haven&apos;t received an enrollment
          code, ask your teacher for one.
        </Text>
      </div>
      <div className='px-x1'>
        <Input
          type='text'
          placeholder='e.g. WGT-SKR'
          value={code}
          error={!!error}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setCode(e.target.value);
            if (error) setError(null);
          }}
          aria-label='Class code'
        />
        {error && (
          <Text size='s' color='negative' className='mt-x0-half'>
            {error}
          </Text>
        )}
      </div>
      <Button
        className='w-full'
        color='primary'
        size='l'
        roundness='pill'
        disabled={!code.trim() || isSubmitting}
        onClick={handleSubmit}
      >
        <Text color='primary-inverse-static'>{isSubmitting ? 'Enrolling...' : 'Submit'}</Text>
      </Button>
    </OnboardingPageLayout>
  );
}
